import React, { useRef, useState } from 'react';
import { toast } from 'sonner';
import { FileUp, Loader2, Sparkles, X, FileText } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { runAiJob } from '../../lib/aiJob';

const OK = ['pdf', 'doc', 'docx'];
const MAX = 5;
const ext = (n) => (n.split('.').pop() || '').toLowerCase();

// Attach Word/PDF files when creating a case; the AI reads them and proposes fields + strategy
export const IntakeUpload = ({ onResult, disabled, endpoint = '/crm/intake', label = 'Adjuntar documentos del caso', cta = 'Analizar con IA y completar el caso', testid = 'intake' }) => {
  const { api } = useAuth();
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const input = useRef(null);

  const add = (list) => {
    const picked = Array.from(list || []);
    const bad = picked.find((f) => !OK.includes(ext(f.name)));
    if (bad) return toast.error(`«${bad.name}»: solo Word (.doc, .docx) o PDF`);
    const big = picked.find((f) => f.size > 10 * 1024 * 1024);
    if (big) return toast.error(`«${big.name}» supera 10 MB`);
    const next = [...files, ...picked].slice(0, MAX);
    if (files.length + picked.length > MAX) toast.error(`Máximo ${MAX} documentos`);
    setFiles(next);
    setDone(null);
  };

  const analyze = async () => {
    const form = new FormData();
    files.forEach((f) => form.append('files', f));
    setBusy(true);
    try {
      const { data } = await api.post(endpoint, form, { timeout: 120000 });
      const r = await runAiJob(api, null, null, { jobId: data.job_id, timeout: 300000 });
      setDone(r.files || files.map(() => ({ read_method: 'texto' })));
      onResult(r);
      toast.success('Documentos analizados: revisa lo propuesto antes de aplicarlo');
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || 'No se pudieron analizar los documentos');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid={`${testid}-upload`} className="min-w-0 w-full rounded-xl border border-dashed border-slate-600 bg-slate-800/40 p-3 space-y-2">
      <button type="button" data-testid={`${testid}-pick-btn`} disabled={busy || disabled} onClick={() => input.current?.click()}
        className="flex w-full min-w-0 items-center gap-2 text-left text-sm text-slate-300 hover:text-amber-300 transition-colors disabled:opacity-50">
        <FileUp className="h-4 w-4 shrink-0" />{label} (Word o PDF, hasta {MAX})
      </button>
      <input ref={input} data-testid={`${testid}-file-input`} type="file" multiple accept=".pdf,.doc,.docx" className="hidden" onChange={(e) => { add(e.target.files); e.target.value = ''; }} />
      {files.length > 0 && (
        <ul className="min-w-0 w-full space-y-1">
          {files.map((f, i) => {
            const info = done?.[i];
            return (
              <li key={`${f.name}-${i}`} data-testid={`${testid}-file-${i}`} className="flex min-w-0 w-full items-center gap-2 text-xs text-slate-300">
                <FileText className="h-3.5 w-3.5 shrink-0 text-slate-500" /><span title={f.name} data-testid={`${testid}-filename-${i}`} className="min-w-0 flex-1 truncate">{f.name}</span>
                {info && <span className="shrink-0 text-[10px] text-slate-500">{info.read_method === 'lectura_ia' ? 'escaneado · leído con IA' : 'texto leído'}</span>}
                {!busy && !done && <button type="button" aria-label={`Quitar ${f.name}`} data-testid={`${testid}-remove-${i}`} onClick={() => setFiles(files.filter((_, j) => j !== i))} className="shrink-0 text-slate-500 hover:text-red-400"><X className="h-3.5 w-3.5" /></button>}
              </li>
            );
          })}
        </ul>
      )}
      {files.length > 0 && !done && (
        <button type="button" data-testid={`${testid}-analyze-btn`} onClick={analyze} disabled={busy}
          className="flex items-center gap-1.5 rounded-full bg-amber-500 px-3 py-1 text-xs font-semibold text-slate-950 hover:bg-amber-400 disabled:opacity-60">
          {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Sparkles className="h-3 w-3" />}{busy ? 'Leyendo documentos y preparando la estrategia...' : cta}
        </button>
      )}
    </div>
  );
};
